import { CompositeSessionInfoSelectors, SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { isUnpublishedWorkbookDraft } from '~src/drafts/utils/DraftUtils';
import { IViewBase } from '~src/views/models/IViewInfo';
import { Views } from '~src/views/resources/Views';

import { AppState } from '../../bootstrap/redux/state/AppState';
import { IDatasourceActions } from '../../datasource/ServerApi/types/IDatasourceActions';
import { IUserInfo } from '../../userManagement/users/models/IUserInfo';
import { IWorkbookActions } from '../../workbook/ServerApi/types/IWorkbookActions';
import { IContentInfo } from '../models/IContentInfo';
import { IRange } from '../models/IRange';
import { IOwnedItemInfo } from '../models/resourceInfoParts';

export interface SliceInfo {
  items: ReadonlyArray<IContentInfo>;
  range: IRange;
}

export function getOwnerIdForView(item: IContentInfo | IViewBase): string {
  if (!item) {
    return null;
  }
  if (Views.isViewBase(item)) {
    return (item.workbook && item.workbook.owner) ? item.workbook.owner.id : null;
  }
  return item.owner ? item.owner.id : null;
}

export function isOwnedItemInfo(item: any): item is IOwnedItemInfo {
  return item && item.hasOwnProperty('owner');
}

export function getOwnerForItem(item: any): IUserInfo {
  if (isOwnedItemInfo(item)) {
    return item.owner;
  }
  return null;
}

export function currentUserIsOwner(state: AppState, item: any): boolean {
  const owner = getOwnerForItem(item);
  const ownerId = owner && owner.id;
  return ownerId === SessionUserSelectors.sessionUserId(state);
}

export function currentUserIsOwnerOrAdmin(state: AppState, item: any): boolean {
  return currentUserIsOwner(state, item) || currentUserIsAdmin(state);
}

export function currentUserIsAdmin(state: AppState): boolean {
  return SessionUserSelectors.isSessionServerAdmin(state)
    || SessionSiteSelectors.isSessionSiteAdmin(state);
}

export function hasExtractRefreshPermission(
  state: AppState,
  item: any,
  availableActions: IWorkbookActions | IDatasourceActions,
): boolean {
    return currentUserIsOwnerOrAdmin(state, item)
      || (availableActions && availableActions.allowExtractRefresh);
}

export function hasRunNowPermission(state: AppState): boolean {
  // Backend handles all the complex logic for determining if it's actually enabled
  return CompositeSessionInfoSelectors.isRunNowEnabled(state);
}

/// Function that normalizes the passed items, start index, and end index by removing all unpublished workbook
/// draft items. The items we receive from the initial and sliced content requests do NOT include unpublished
/// workbook drafts; the drafts are received from CDMS and inserted into the items array, which is then rendered.
/// In order to request the correct start and end indices for additional sliced content, we need to first remove
/// all unpublished workbook draft items.
export function normalizeSliceInfo(items: ReadonlyArray<IContentInfo>, start: number, end: number): SliceInfo {
  let normalizedItems = items.slice();
  let normalizedStart: number = start;
  let normalizedEnd: number = end;
  let removedItemCount = 0;

  items.forEach((item, index) => {
    if (isUnpublishedWorkbookDraft(item)) {
      // Remove item
      normalizedItems.splice(index - removedItemCount, 1);
      removedItemCount += 1;

      if (index <= start) {
        // Decrement when the item is contained within [ 0, start ] inclusive
        normalizedStart -= 1;
      }

      if (index <= end) {
        // Decrement when the item is contained within [ 0, end ] inclusive
        normalizedEnd -= 1;
      }
    }
  });

  return {
    items: normalizedItems,
    range: {
      start: Math.max(normalizedStart, 0),
      end: Math.max(normalizedEnd, 0),
    },
  };
}